import { Collection, Database, Q } from '@nozbe/watermelondb';

import Organisation from './Organisation';
import {
    InventoryProductModel,
    InventoryProductPayload,
} from '../../types/InventoryProduct';
import { InventoryProductServiceOptions } from '../../types/dbService';
import { InventoryFiltersObject } from '../../types/inventoryFilters';
import { applyInventoryFiltersQueries } from '../utils';
import {
    addPreparedDataBatch,
    mapRecord,
    mapRecords,
    PreparedInventoryProductData,
} from './helpers/inventoryProduct/common';
import { validateEntityRecords } from './helpers/common/validateEntityRecords';
import {
    logAddInventoryChangeAction,
    logAddInventoryProductAction,
    prepareCreateInventoryChange,
    prepareCreateInventoryProduct,
} from './helpers/inventoryProduct/inventoryProductAdd';
import {
    logDeleteInventoryProductActions,
    prepareDeleteInventoryChanges,
    prepareDeleteInventoryProduct,
} from './helpers/inventoryProduct/inventoryProductDelete';
import {
    logUpdateInventoryProductAction,
    prepareUpdateInventoryProduct,
} from './helpers/inventoryProduct/inventoryProductUpdate';
import { InventoryChangePayload } from '../../types/InventoryChange';

class InventoryProduct {
    private database: Database;
    private collection: Collection<InventoryProductModel>;
    private table = 'inventory_products' as const;
    private options: InventoryProductServiceOptions;

    constructor(options: InventoryProductServiceOptions) {
        this.database = options.database;
        this.collection = options.database.collections.get(this.table);
        this.options = options;
    }

    getAll() {
        return this.collection.query().fetch();
    }

    getByID(id: string) {
        return this.collection.find(id);
    }

    getFilteredProducts(filters: InventoryFiltersObject) {
        return this.collection
            .query(...applyInventoryFiltersQueries(filters))
            .fetch();
    }

    async getByParam(param: string, value: any) {
        return this.collection.query(Q.where(param, value)).fetch();
    }

    async add(
        payload: InventoryProductPayload,
        userId: string,
        postPayload?: {
            inventoryChange?: Omit<
                InventoryChangePayload,
                'inventoryProductId'
            >;
        },
    ) {
        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        return await this.database.write(async () => {
            const createdInventoryProduct = prepareCreateInventoryProduct({
                payload,
                userId,
                organisationID,
                collection: this.collection,
            });
            const preparedData: PreparedInventoryProductData[] = [
                mapRecord(createdInventoryProduct, this.table, 'add'),
            ];

            const createdInventoryChange = prepareCreateInventoryChange({
                payload: postPayload?.inventoryChange,
                inventoryProduct: createdInventoryProduct,
                userId,
                organisationID,
                database: this.database,
            });
            if (createdInventoryChange) {
                preparedData.push(
                    mapRecord(
                        createdInventoryChange,
                        'inventory_changes',
                        'add',
                    ),
                );
            }

            validateEntityRecords(
                preparedData,
                this.options.validation,
            );
            await addPreparedDataBatch(preparedData, this.database);

            logAddInventoryProductAction({
                inventoryProduct: createdInventoryProduct,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });

            if (createdInventoryChange) {
                logAddInventoryChangeAction({
                    inventoryChange: createdInventoryChange,
                    logDBAction: this.options.logDBAction,
                });
            }

            return createdInventoryProduct;
        });
    }

    async update(id: string, payload: InventoryProductPayload, userId: string) {
        const inventoryProductElement = await this.getByID(id);

        const organisationService = new Organisation({
            database: this.database,
            imageService: this.options.imageService,
            logDBAction: this.options.logDBAction,
            validation: this.options.validation,
        });

        const organisation = await organisationService.get();
        const { id: organisationID } = organisation[0];

        await this.database.write(async () => {
            const inventoryProductToUpdate = prepareUpdateInventoryProduct({
                inventoryProductElement,
                payload,
                userId,
                organisationID,
            });
            const preparedData: PreparedInventoryProductData[] = [
                mapRecord(inventoryProductToUpdate, this.table, 'update'),
            ];

            validateEntityRecords(
                preparedData,
                this.options.validation,
            );
            await addPreparedDataBatch(preparedData, this.database);

            logUpdateInventoryProductAction({
                inventoryProductElement,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }

    async deleteByID(id: string) {
        const inventoryProductElement = await this.getByID(id);
        const relatedInventoryChanges =
            await inventoryProductElement.inventoryChanges.fetch();

        await this.database.write(async () => {
            const inventoryProductToDelete = prepareDeleteInventoryProduct({
                inventoryProductElement,
            });

            const preparedData: PreparedInventoryProductData[] = [
                mapRecord(inventoryProductToDelete, this.table, 'delete'),
            ];
            const inventoryChangesToDelete = prepareDeleteInventoryChanges({
                relatedInventoryChanges,
            });

            if (inventoryChangesToDelete.length > 0) {
                preparedData.push(
                    ...mapRecords(
                        inventoryChangesToDelete,
                        'inventory_changes',
                        'delete',
                    ),
                );
            }

            validateEntityRecords(
                preparedData,
                this.options.validation,
            );
            await addPreparedDataBatch(preparedData, this.database);

            logDeleteInventoryProductActions({
                inventoryProductElement,
                inventoryChangesToDelete,
                logDBAction: this.options.logDBAction,
                tableName: this.table,
            });
        });
    }
}

export default InventoryProduct;
